import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  Project,
  registryApi,
  routerApi,
  useListMyProjectInvitationsQuery,
  useCreateProjectMutation,
  useDeleteProjectMutation,
  useListProjectsQuery,
} from '../../redux/services/registry';
import { getCurrentProjectId, setCurrentProjectId as setProjectStorage } from './project-storage';
import { getRouterApiKey, setRouterApiKey } from './router-api-key-storage';
import { useAuth } from './auth/auth.provider';

interface CreateProjectInput {
  projectId: string;
  name: string;
  description?: string;
}

interface ProjectContextProps {
  projects: Project[];
  hasProjects: boolean;
  hasPendingInvitations: boolean;
  currentProjectId: string | null;
  currentProject: Project | null;
  loadingProjects: boolean;
  createProject: (input: CreateProjectInput) => Promise<Project>;
  deleteProject: (projectId: string, cascade?: boolean) => Promise<void>;
  selectProject: (projectId: string) => void;
  refetchProjects: () => void;
}

const ProjectContext = createContext<ProjectContextProps>({
  projects: [],
  hasProjects: false,
  hasPendingInvitations: false,
  currentProjectId: null,
  currentProject: null,
  loadingProjects: false,
  createProject: async () => {
    throw new Error('ProjectContext not initialized');
  },
  deleteProject: async () => {
    throw new Error('ProjectContext not initialized');
  },
  selectProject: () => {},
  refetchProjects: () => {},
});

export const ProjectContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const userStorageKey = user?.id || user?.email || null;
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(() => getCurrentProjectId(userStorageKey));
  const previousProjectIdRef = useRef<string | null>(currentProjectId);

  const { data: projects = [], isLoading: loadingProjects, refetch } = useListProjectsQuery();
  const { data: invitations = [] } = useListMyProjectInvitationsQuery();
  const [createProjectMutation] = useCreateProjectMutation();
  const [deleteProjectMutation] = useDeleteProjectMutation();

  const selectProject = useCallback(
    (projectId: string) => {
      if (projectId === currentProjectId) {
        return;
      }
      setCurrentProjectId(projectId);
      setProjectStorage(projectId, userStorageKey);
    },
    [currentProjectId, userStorageKey],
  );

  useEffect(() => {
    const storedProjectId = getCurrentProjectId(userStorageKey);
    setCurrentProjectId((previousProjectId) => (previousProjectId === storedProjectId ? previousProjectId : storedProjectId));
    previousProjectIdRef.current = storedProjectId;
  }, [userStorageKey]);

  useEffect(() => {
    const previousProjectId = previousProjectIdRef.current;
    if (previousProjectId === currentProjectId) {
      return;
    }

    previousProjectIdRef.current = currentProjectId;
    dispatch(registryApi.util.resetApiState());
    dispatch(routerApi.util.resetApiState());
  }, [currentProjectId, dispatch]);

  useEffect(() => {
    if (!projects.length) {
      if (loadingProjects) {
        return;
      }
      if (currentProjectId) {
        setCurrentProjectId(null);
        setProjectStorage(null, userStorageKey);
      }
      return;
    }

    const exists = currentProjectId && projects.some((project) => project.id === currentProjectId);
    if (!exists) {
      const nextProjectId = projects[0].id;
      setCurrentProjectId(nextProjectId);
      setProjectStorage(nextProjectId, userStorageKey);
    }
  }, [projects, currentProjectId, loadingProjects, userStorageKey]);

  // One-time migration: copy router API keys stored under old slug to new MongoDB _id key
  useEffect(() => {
    if (!projects.length) return;
    projects.forEach((project) => {
      const slug = (project as any).projectId as string | undefined;
      const id = project.id;
      if (!slug || !id || slug === id) return;
      const oldKey = getRouterApiKey(slug);
      if (oldKey && !getRouterApiKey(id)) {
        setRouterApiKey(id, oldKey);
        setRouterApiKey(slug, null); // clean up old key
      }
    });
  }, [projects]);

  const createProject = useCallback(
    async (input: CreateProjectInput): Promise<Project> => {
      const createdProject = await createProjectMutation(input).unwrap();
      selectProject(createdProject.id);
      return createdProject;
    },
    [createProjectMutation, selectProject],
  );

  const deleteProject = useCallback(
    async (projectId: string, cascade: boolean = true) => {
      await deleteProjectMutation({ projectId, cascade }).unwrap();

      const remainingProjects = projects.filter((project) => project.id !== projectId);
      if (currentProjectId === projectId) {
        const fallbackProjectId = remainingProjects[0]?.id ?? null;
        setCurrentProjectId(fallbackProjectId);
        setProjectStorage(fallbackProjectId, userStorageKey);
      }
    },
    [currentProjectId, deleteProjectMutation, projects, userStorageKey],
  );

  const currentProject = useMemo(
    () => projects.find((project) => project.id === currentProjectId) || null,
    [projects, currentProjectId],
  );

  return (
    <ProjectContext.Provider
      value={{
        projects,
        hasProjects: projects.length > 0,
        hasPendingInvitations: invitations.length > 0,
        currentProjectId,
        currentProject,
        loadingProjects,
        createProject,
        deleteProject,
        selectProject,
        refetchProjects: () => {
          void refetch();
        },
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export const useProjectContext = () => useContext(ProjectContext);
